import { FC } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useUserState } from "./common/UserContext";
import { UserPreferencesView } from "./components/user/UserPreferencesView";
import { AdminUserGroupsView } from "./components/admin/AdminUserGroupsView";
import { AdminUserView } from "./components/admin/AdminUserView";
import UploadSummaryView from "./components/orderform/UploadSummaryView";
import { OrderFormScoreView as OrderFormScoreViewStatic } from "./components/orderform/OrderFormScoreView";
import { OrderFormScoreView } from "./components/orderform/score/OrderFormScoreView";
import { WristbandLogin } from "./components/login/WristbandLogin";
import { Login } from "./components/login/Login";
import { FeatureFlagsView } from "./components/admin/FeatureFlagsView";
import { isOnboarding, UserRole } from "./types/UserGroup";
import { UserImpersonationView } from "./components/admin/UserImpersonationView";
import { IndustryView } from "./components/superadmin/IndustryView";
import AppLayout, { NavKey, WithActiveNav } from "./components/layout/AppLayout";
import { Redirect } from "react-router6-redirect";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import { NegotiationDetailsView } from "./components/negotiations/NegotiationDetailsView";
import { NotificationsView } from "./components/notifications/NotificationsView";
import { OrganizationView } from "./components/vendor/OrganizationView";
import { VendorDetails } from "./components/vendor/VendorDetails";
import { VendorDetails as VendorDetailsDemo } from "./components/vendor-v2/VendorDetails";
import { OrderFormsView as OrderFormsViewV2 } from "./components/orderform/OrderFormsViewV2";
import { SupplierDetailView } from "./components/supplier/detail/SupplierDetailView";
import { SupplierView } from "./components/supplier/list/SupplierView";
import { VendorsView } from "./components/vendor/list/VendorsView";
import { NegotiationsView } from "./components/negotiations/v2/list/NegotiationsView";
import { OrderFormSummary } from "./components/orderform/OrderFormSummary";
import { TacticView } from "./components/tactic/detail-v2/TacticView";
import { TacticListView } from "./components/tactic/list/TacticListView";
import { OrderFormDetailsView as OrderFormDetailsViewV2 } from "./components/orderform/detail-v2/OrderFormDetailsView";
import { OrganizationDetailView } from "./components/vendor/OrganizationDetailView";
import { ProjectListView } from "./components/project/ProjectListView";
import { ProjectView } from "./components/project/ProjectView";
import {
    DemoBriefListView,
    DemoBriefDetailView,
} from "./components/buyerbrief/demo/DemoBuyerBriefs";
import { DemoDealIntakeView } from "./components/dealintake/demo/DemoDealIntake";
import {
    DemoReportsListView,
    DemoReportDetailView,
} from "./components/reports/demo/DemoReports";
import { Playground } from "./design-system/playground/Playground";
import {
    TailSpendLayout,
    TailSpendNegotiationsView,
    DealDetailView as TailSpendDealDetailView,
    TailSpendSuppliersView,
    TailSpendReportingView,
    GuardrailsView as TailSpendGuardrailsView,
    SetupView as TailSpendSetupView,
} from "./components/tailspend/demo";

export const AppRoutes: FC = () => {
    const { me } = useUserState();

    const indexElement = isOnboarding(me.roles ?? []) ? (
        <Navigate to="/project" />
    ) : (
        <NegotiationsView />
    );

    return (
        <>
            <Routes>
                <Route path="internal">
                    <Route path="login" element={<Login />} />
                </Route>
                {/* Tail-Spend demo brings its own shell — mount above <AppLayout/> */}
                <Route
                    element={
                        <ProtectedRoute accessType={"GRANT"} roles={[UserRole.SUPER_ADMIN]} />
                    }
                >
                    <Route
                        path="superadmin/demo/tail-spend"
                        element={<TailSpendLayout />}
                    >
                        <Route index element={<Navigate to="negotiations" replace />} />
                        <Route path="negotiations" element={<TailSpendNegotiationsView />} />
                        <Route
                            path="negotiations/:dealId"
                            element={<TailSpendDealDetailView />}
                        />
                        <Route path="reporting" element={<TailSpendReportingView />} />
                        <Route path="suppliers" element={<TailSpendSuppliersView />} />
                        <Route
                            path="suppliers/:supplierId"
                            element={<TailSpendSuppliersView />}
                        />
                        <Route path="guardrails" element={<TailSpendGuardrailsView />} />
                        <Route path="setup" element={<TailSpendSetupView />} />
                    </Route>
                </Route>
                <Route path="/" element={<AppLayout />}>
                    {/* These routes are always available */}
                    <Route element={<WithActiveNav navKey={NavKey.Negotiations} />}>
                        <Route index element={indexElement} />
                    </Route>
                    <Route path="login" element={<WristbandLogin />} />
                    <Route
                        path="preferences"
                        element={<WithActiveNav navKey={NavKey.Preferences} />}
                    >
                        <Route index element={<UserPreferencesView />} />
                    </Route>
                    <Route path="order" element={<WithActiveNav navKey={NavKey.Projects} />}>
                        <Route path="new" element={<OrderFormsViewV2 />} />
                    </Route>
                    <Route path="project" element={<WithActiveNav navKey={NavKey.Projects} />}>
                        <Route index element={<ProjectListView />} />
                        <Route path=":projectId" element={<ProjectView />} />
                        <Route path=":projectId/intake" element={<ProjectView />} />
                        <Route path=":projectId/analysis/:version" element={<ProjectView />} />
                    </Route>

                    {/* These routes are only available if you are NOT onboarding */}
                    <Route
                        element={
                            <ProtectedRoute accessType={"DENY"} roles={[UserRole.ONBOARDING]} />
                        }
                    >
                        <Route path="dashboard" element={<Navigate to="/" />} />
                        <Route
                            path="notifications"
                            element={<WithActiveNav navKey={NavKey.Notifications} />}
                        >
                            <Route index element={<NotificationsView />} />
                        </Route>

                        <Route path="order" element={<WithActiveNav navKey={NavKey.Projects} />}>
                            <Route path=":orderFormId" element={<OrderFormDetailsViewV2 />} />
                            <Route path="score/:orderFormId" element={<OrderFormScoreView />} />
                        </Route>

                        <Route path="vendor" element={<WithActiveNav navKey={NavKey.Vendors} />}>
                            <Route index element={<VendorsView />} />
                            <Route path=":vendorKey" element={<VendorDetails />} />
                        </Route>

                        <Route
                            path="negotiations"
                            element={<WithActiveNav navKey={NavKey.Negotiations} />}
                        >
                            <Route index element={<Navigate to="/" />} />
                            <Route path=":negotiationId" element={<NegotiationDetailsView />} />
                        </Route>

                        <Route
                            path="supplier"
                            element={<WithActiveNav navKey={NavKey.BuyerBriefs} />}
                        >
                            <Route index element={<SupplierView />} />
                            <Route path=":id" element={<SupplierDetailView />} />
                        </Route>

                        <Route path="tactic" element={<WithActiveNav navKey={NavKey.Tactics} />}>
                            <Route index element={<TacticListView />} />
                            <Route path=":id" element={<TacticView />} />
                        </Route>

                        {/* These routes are only available to admins */}
                        <Route
                            element={
                                <ProtectedRoute accessType={"GRANT"} roles={[UserRole.ADMIN]} />
                            }
                        >
                            <Route
                                path="order"
                                element={<WithActiveNav navKey={NavKey.Projects} />}
                            >
                                <Route path="new/:uploadId" element={<UploadSummaryView />} />
                            </Route>

                            <Route path="admin">
                                <Route
                                    path="users"
                                    element={<WithActiveNav navKey={NavKey.Users} />}
                                >
                                    <Route index element={<AdminUserGroupsView />} />
                                    <Route path=":userId" element={<AdminUserView />} />
                                </Route>
                            </Route>
                        </Route>

                        {/* These routes are only available to Super Admins */}
                        <Route
                            element={
                                <ProtectedRoute
                                    accessType={"GRANT"}
                                    roles={[UserRole.SUPER_ADMIN]}
                                />
                            }
                        >
                            <Route path="superadmin">
                                <Route
                                    path="demo/brief"
                                    element={<WithActiveNav navKey={NavKey.BuyerBriefs} />}
                                >
                                    <Route index element={<DemoBriefListView />} />
                                    <Route path=":id" element={<DemoBriefDetailView />} />
                                </Route>
                                <Route
                                    path="demo/deal-intake"
                                    element={<WithActiveNav navKey={NavKey.SuperAdminMenu} />}
                                >
                                    <Route index element={<DemoDealIntakeView />} />
                                </Route>
                                <Route
                                    path="demo/reports"
                                    element={<WithActiveNav navKey={NavKey.SuperAdminMenu} />}
                                >
                                    <Route index element={<DemoReportsListView />} />
                                    <Route path=":id" element={<DemoReportDetailView />} />
                                </Route>
                                <Route
                                    path="industry"
                                    element={<WithActiveNav navKey={NavKey.Industry} />}
                                >
                                    <Route index element={<IndustryView />} />
                                </Route>
                                <Route
                                    path="summary"
                                    element={<WithActiveNav navKey={NavKey.Projects} />}
                                >
                                    <Route path=":orderFormId" element={<OrderFormSummary />} />
                                </Route>
                                <Route
                                    path="order"
                                    element={<WithActiveNav navKey={NavKey.Projects} />}
                                >
                                    <Route
                                        path="score/:orderFormId/static"
                                        element={<OrderFormScoreViewStatic />}
                                    />
                                </Route>
                                <Route path="vendor" element={<WithActiveNav navKey={NavKey.Vendors} />}>
                                    <Route path=":vendorKey" element={<VendorDetailsDemo />} />
                                </Route>
                                <Route
                                    path="playground"
                                    element={<WithActiveNav navKey={NavKey.SuperAdminMenu} />}
                                >
                                    <Route index element={<Playground />} />
                                </Route>
                            </Route>

                            <Route path="admin">
                                <Route path="orgs" element={<WithActiveNav navKey={NavKey.Orgs} />}>
                                    <Route index element={<OrganizationView />} />
                                    <Route
                                        path=":orgId"
                                        element={<OrganizationDetailView />}
                                    ></Route>
                                    <Route
                                        path=":orgId/impersonate"
                                        element={<OrganizationDetailView />}
                                    ></Route>
                                </Route>
                                <Route
                                    path="flags"
                                    element={<WithActiveNav navKey={NavKey.FeatureFlags} />}
                                >
                                    <Route index element={<FeatureFlagsView />} />
                                </Route>
                                <Route
                                    path="impersonation"
                                    element={<WithActiveNav navKey={NavKey.Impersonation} />}
                                >
                                    <Route index element={<UserImpersonationView />} />
                                </Route>
                            </Route>
                        </Route>
                    </Route>

                    {/* These are just redirects from old paths with "/app" in them which has been deprecated */}
                    <Route path="app">
                        <Route index element={<Redirect to="/" />} />
                        <Route
                            path="order/:orderFormId"
                            element={<Redirect to="/order/:orderFormId" />}
                        />
                        <Route path="order/new" element={<Redirect to="/order/new" />} />
                        <Route
                            path="order/new/:uploadId"
                            element={<Redirect to="/order/new/:uploadId" />}
                        />
                        <Route
                            path="order/score/:orderFormId"
                            element={<Redirect to="/order/score/:orderFormId" />}
                        />
                        <Route path="dashboard" element={<Redirect to="/" />} />
                        <Route
                            path="vendor/:vendorKey"
                            element={<Redirect to="/vendor/:vendorKey" />}
                        />
                        <Route path="vendor" element={<Redirect to="/vendor" />} />
                        <Route path="notifications" element={<Redirect to="/notifications" />} />
                        <Route path="preferences" element={<Redirect to="/preferences" />} />
                        <Route path="login" element={<Redirect to="/login" />} />
                        <Route path="negotiations" element={<Redirect to="/" />} />
                        <Route
                            path="negotiations/:negotiationId"
                            element={<Redirect to="/negotiations/:negotiationId" />}
                        />
                        <Route path="supplier" element={<Redirect to="/supplier" />} />
                        <Route path="supplier/:id" element={<Redirect to="/supplier/:id" />} />
                        <Route
                            path="internal/supplier/:id"
                            element={<Redirect to="/internal/supplier/:id" />}
                        />
                    </Route>

                    {/* Any unknown path redirects to the index path */}
                    <Route path="*" element={<Navigate to="/" />} />
                </Route>
            </Routes>
        </>
    );
};
